import { jobJustificationCFOAction } from "@/services/hiring-justification-functions";
import { ApprovalStatus } from "@/services/invoice-functions";
import { JobJustificationCFOActionParams } from "@/types/hiring-justification.types";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaCheck, FaXmark } from "react-icons/fa6";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form";
import { Textarea } from "../ui/textarea";

const formSchema = z.object({
	note: z.string().min(1, {
		message: "Notes are required",
	}),
});

const CFOJobApproveOrReject: React.FC<{
	jobId: number;
	accessToken: string;
}> = ({ jobId, accessToken }) => {
	const [open, setOpen] = useState(false);
	const [approvalStatus, setApprovalStatus] =
		useState<ApprovalStatus>("Approved");

	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const actionJobJustification = useMutation({
		mutationKey: ["job", "cfo-action", jobId],
		mutationFn: (data: JobJustificationCFOActionParams) =>
			jobJustificationCFOAction(accessToken, data),
		onSuccess: () => {
			queryClient.invalidateQueries(["job"]);
			toast.success("Job Justification Actioned", {
				description: `Job ${jobId} has successfully been submitted`,
			});
			setOpen(false);
			form.reset();
			actionJobJustification.reset();
			navigate("/cfo-job-approvals");
		},
		onError: () => {
			toast.error("Error submitting remark");
		},
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			note: "",
		},
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		if (approvalStatus === "Approved") {
			actionJobJustification.mutate({
				cfo_approval_note: data.note,
				jobid: jobId,
			});
			return;
		}
		actionJobJustification.mutate({
			cfo_rejected_note: data.note,
			jobid: jobId,
		});
	};

	const disabled = () => {
		if (actionJobJustification.isLoading) return true;
		if (actionJobJustification.isSuccess) return true;
		return false;
	};

	useEffect(() => {
		form.reset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	return (
		<Dialog open={open} onOpenChange={(open) => setOpen(open)}>
			<div className="flex gap-2">
				<DialogTrigger asChild>
					<Button onClick={() => setApprovalStatus("Approved")}>Approve</Button>
				</DialogTrigger>
				<DialogTrigger asChild>
					<Button onClick={() => setApprovalStatus("Rejected")} variant="secondary">
						Disapprove
					</Button>
				</DialogTrigger>
			</div>
			<DialogContent className="sm:max-w-md">
				<DialogHeader className="text-left">
					{approvalStatus === "Approved" ? (
						<>
							<DialogTitle className="text-green-500 flex gap-2 items-center mb-1">
								<div className="bg-green-200 p-1 rounded-full">
									<FaCheck className="text-xl" />
								</div>
								Approving Job Justification
							</DialogTitle>
							<DialogDescription>Add your notes here.</DialogDescription>
						</>
					) : (
						<>
							<DialogTitle className="text-red-500 flex gap-2 items-center mb-1">
								<div className="bg-red-200 p-1 rounded-full">
									<FaXmark className="text-xl" />
								</div>
								Disapproving Job Justification
							</DialogTitle>
							<DialogDescription>Add your notes here.</DialogDescription>
						</>
					)}
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<div className="flex items-center space-x-2 mb-4">
							<div className="grid flex-1 gap-2">
								<FormField
									name="note"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Notes</FormLabel>
											<FormControl>
												<Textarea {...field} disabled={disabled()} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>

						<DialogFooter className="sm:justify-end">
							<DialogClose asChild>
								<Button type="button" variant="ghost">
									Cancel
								</Button>
							</DialogClose>
							<Button type="submit" disabled={disabled()}>
								{disabled() ? "Submitting..." : "Submit"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default CFOJobApproveOrReject;
